import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, RefreshCw, Trash2 } from 'lucide-react';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import { useAuth } from '../context/AuthContext';
import proposalService from '../services/proposalService';

const AIChatPage = () => {
  const { notification, showNotification, hideNotification } = useNotification();
  const { user } = useAuth();
  const chatEndRef = useRef(null);

  const [chatHistory, setChatHistory] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() || loading) return;

    const userMessage = message;
    setMessage('');

    // Add user message to chat
    const updatedHistory = [...chatHistory, { role: 'user', content: userMessage }];
    setChatHistory(updatedHistory);

    try {
      setLoading(true);
      const data = await proposalService.aiChat(userMessage, chatHistory, user?.role || '');
      setChatHistory(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      showNotification(error.message || 'Error al comunicarse con la IA', 'error');
      // Remove last user message on error
      setChatHistory(chatHistory);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setChatHistory([]);
  };

  return (
    <div className="animate-fadeIn" style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">🧠 Xtreme-AI</h1>
          <p className="text-gray-600">Tu asistente inteligente de RedXtreme • Pregunta lo que quieras</p>
        </div>
        <button onClick={clearChat} className="btn btn-outline" title="Limpiar chat">
          <Trash2 size={16} /> Limpiar Chat
        </button>
      </div>

      {/* Chat Container */}
      <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {chatHistory.length === 0 && (
            <div className="text-center text-gray-400 py-16">
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🧠</div>
              <h3 className="text-xl font-semibold mb-2">¡Hola! Soy Xtreme-AI</h3>
              <p className="text-gray-500 mb-6">Tu asistente inteligente de RedXtreme. Puedo ayudarte con:</p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {['💻 Consultas técnicas', '💰 Precios y cotizaciones', '📊 Análisis de proyectos', '🛠️ Arquitectura de software'].map((item, i) => (
                  <span key={i} style={{ 
                    padding: '8px 16px', backgroundColor: '#f3f4f6', borderRadius: '20px', 
                    fontSize: '14px', color: '#4b5563' 
                  }}>{item}</span>
                ))}
              </div>
            </div>
          )}

          {chatHistory.map((msg, i) => (
            <div key={i} style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: '16px'
            }}>
              {msg.role === 'assistant' && (
                <div style={{ 
                  width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#4f7942',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginRight: '8px', flexShrink: 0, fontSize: '14px'
                }}>🧠</div>
              )}
              <div style={{
                maxWidth: '70%',
                padding: '12px 16px',
                borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                backgroundColor: msg.role === 'user' ? '#4f7942' : '#f3f4f6',
                color: msg.role === 'user' ? 'white' : '#1f2937',
                fontSize: '14px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {msg.content}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{ 
                width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#4f7942',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px'
              }}>🧠</div>
              <div style={{ padding: '12px 16px', backgroundColor: '#f3f4f6', borderRadius: '16px 16px 16px 4px' }}>
                <RefreshCw size={16} className="animate-spin" style={{ color: '#9ca3af' }} />
              </div>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSend} style={{ 
          display: 'flex', gap: '8px', padding: '16px 20px', 
          borderTop: '1px solid #e5e7eb', backgroundColor: '#fafafa' 
        }}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={loading ? 'Xtreme-AI está pensando...' : 'Escribe tu pregunta...'}
            className="form-input"
            style={{ flex: 1 }}
            disabled={loading}
          />
          <button type="submit" className="btn btn-primary" disabled={loading || !message.trim()}>
            {loading ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </form>
      </div>

      <NotificationSystem notification={notification} onClose={hideNotification} />
    </div>
  );
};

export default AIChatPage;
